<script lang="ts" setup>
const { tournament } = defineProps<{ tournament: Tournament }>()

const { data: ongoingGames, refresh: refreshOngoing } = await useApi('/tournaments/{tournament}/ongoing-games', {
  path: {
    tournament: tournament.id,
  },
  key: `ongoing-games-${tournament.id}`,
})

const completedGamesPage = ref(1)
const { data: completedGames, refresh: refreshCompleted } = await useApi('/tournaments/{tournament}/completed-games', {
  path: {
    tournament: tournament.id,
  },
  params: {
    page: completedGamesPage,
  },
  key: `completed-games-${tournament.id}-${completedGamesPage.value}`,
})

const upcomingGamesPage = ref(1)
const { data: upcomingGames, refresh: refreshUpcoming } = await useApi('/tournaments/{tournament}/upcoming-games', {
  path: {
    tournament: tournament.id,
  },
  params: {
    page: upcomingGamesPage,
    per_page: 6,
  },
  key: `upcoming-games-${tournament.id}-${upcomingGamesPage.value}`,
})

const divCompletedGames = ref()
watch(completedGamesPage, () => {
  if (divCompletedGames.value) {
    divCompletedGames.value.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
})

const divUpcomingGames = ref()
watch(upcomingGamesPage, () => {
  if (divUpcomingGames.value) {
    divUpcomingGames.value.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
})

onMounted(() => {
  const interval = setInterval(async () => {
    if (!tournament.complete) {
      refreshOngoing()
      refreshCompleted()
      refreshUpcoming()
    }
  }, 15000)

  onBeforeUnmount(() => {
    clearInterval(interval)
  })
})
</script>

<template>
  <div class="flex flex-col gap-4">
    <UiCard
      v-if="!tournament.complete"
      :title="$t('Ongoing games')"
    >
      <template #content>
        <UiCardContent>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div
              v-for="game in ongoingGames"
              :key="game.id"
              class="flex flex-col"
            >
              <div>
                {{ $t('Board:') }} <strong>{{ game.board.name }}</strong>
              </div>
              <GamescreenGameCard
                :game="game"
                :boards="[]"
                :for-display="true"
              />
            </div>
          </div>
        </UiCardContent>
      </template>
    </UiCard>

    <UiCard
      v-if="!tournament.complete"
      :title="$t('Upcoming games')"
    >
      <template #content>
        <UiCardContent>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div
              v-for="game in upcomingGames?.data ?? []"
              :key="game.id"
              class="flex flex-col"
            >
              {{ game.round_label }}
              <GamescreenGameCard
                :game="game"
                :boards="[]"
                :for-display="true"
              />
            </div>
          </div>
          <Pagination
            v-if="upcomingGames?.meta"
            v-model="upcomingGamesPage"
            :meta="upcomingGames.meta"
          />
        </UiCardContent>
      </template>
    </UiCard>

    <UiCard :title="$t('Completed games')">
      <template #content>
        <UiCardContent>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div
              v-for="game in completedGames?.data ?? []"
              :key="game.id"
              class="flex flex-col"
            >
              {{ game.round_label }}
              <GamescreenGameCard
                :game="game"
                :boards="[]"
                :for-display="true"
              />
            </div>
          </div>
          <Pagination
            v-if="completedGames?.meta"
            v-model="completedGamesPage"
            :meta="completedGames.meta"
          />
        </UiCardContent>
      </template>
    </UiCard>
  </div>
</template>
